import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

interface LightboxProps {
  src: string;
  alt?: string;
  isOpen: boolean;
  onClose: () => void;
}

const Lightbox: React.FC<LightboxProps> = ({ src, alt = '', isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-brand-ink/95 backdrop-blur-sm"
          onClick={onClose}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 p-2 text-brand-paper/80 hover:text-brand-gold transition-colors"
            aria-label="Close"
          >
            <X className="w-8 h-8" />
          </button>

          <div
            className="w-screen h-screen flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <TransformWrapper
              initialScale={1}
              minScale={1}
              maxScale={6}
              wheel={{ disabled: false }}
              pinch={{ disabled: false }}
              panning={{ disabled: false }}
              doubleClick={{ mode: 'toggle' }}
              centerOnInit
              centerZoomedOut
            >
              <TransformComponent
                wrapperClass="!w-screen !h-screen"
                contentClass="!w-full !h-full flex items-center justify-center"
              >
                <motion.img
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  src={src}
                  alt={alt}
                  className="max-w-[95vw] max-h-[90vh] object-contain select-none"
                  draggable={false}
                  style={{ touchAction: 'none' }}
                />
              </TransformComponent>
            </TransformWrapper>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Lightbox;
